import { word } from "@/modules/core/utils/i18n"
import { UserEntity } from "@/modules/profile/userProfile/userEntity"
import type { ProgramType, ProgramVisibility } from "@/modules/programs/types/programTypes"
import { SessionBlockEntityFactory, type SessionBlockEntityType } from "@/modules/sessions/entities/sessionBlockEntity"
import type {
  CreateSessionInputDto,
  UpdateSessionInputDto,
  WebSessionDetailsDtoWithNonNullable,
} from "@/modules/sessions/gateway/programInfiniteSessionDto"
import type { ProgramInfiniteSessionFormikType } from "@/modules/sessions/programSessionFormikType"
import { ProgramInfiniteSessionTypeOf } from "@/modules/sessions/programSessionTypes"
import { SocialPostResumeLightEntity } from "@/modules/social/entities/socialEntity"
import { defaultDateFormat } from "@/modules/utils/dateUtils"
import dayjs from "dayjs"

export class ProgramInfiniteSessionEntity {
  id: string
  name: string
  description: string | null
  date: string | null
  owner: UserEntity
  programId: string
  programType: ProgramType
  programVisibility: ProgramVisibility
  programName: string
  hideSessionContent: boolean
  cardioTag: boolean
  gymnasticTag: boolean
  weightliftingTag: boolean
  index: number | null
  virtualProgramByDateIndex: number | null
  socialPostId: string | null
  socialResume: SocialPostResumeLightEntity | null
  blocks: SessionBlockEntityType[]
  sessionCompletedByUser: boolean

  private constructor(programInfiniteSession: ProgramInfiniteSessionEntity) {
    this.id = programInfiniteSession.id
    this.name = programInfiniteSession.name
    this.description = programInfiniteSession.description
    this.date = programInfiniteSession.date
    this.owner = programInfiniteSession.owner
    this.programId = programInfiniteSession.programId
    this.programType = programInfiniteSession.programType
    this.programVisibility = programInfiniteSession.programVisibility
    this.programName = programInfiniteSession.programName
    this.hideSessionContent = programInfiniteSession.hideSessionContent
    this.cardioTag = programInfiniteSession.cardioTag
    this.gymnasticTag = programInfiniteSession.gymnasticTag
    this.weightliftingTag = programInfiniteSession.weightliftingTag
    this.index = programInfiniteSession.index
    this.virtualProgramByDateIndex = programInfiniteSession.virtualProgramByDateIndex
    this.socialPostId = programInfiniteSession.socialPostId
    this.socialResume = programInfiniteSession.socialResume
    this.blocks = programInfiniteSession.blocks
    this.sessionCompletedByUser = programInfiniteSession.sessionCompletedByUser
  }

  static fromDto(dto: WebSessionDetailsDtoWithNonNullable): ProgramInfiniteSessionEntity {
    return new ProgramInfiniteSessionEntity({
      id: dto.id,
      name: dto.name,
      description: dto.description,
      date: dto.date,
      owner: UserEntity.fromDto(dto.owner),
      programId: dto.programId,
      programType: dto.programType,
      programVisibility: dto.programVisibility,
      programName: dto.programName,
      hideSessionContent: dto.hideSessionContent ?? false,
      cardioTag: dto.cardioTag,
      gymnasticTag: dto.gymnasticTag,
      weightliftingTag: dto.weightliftingTag,
      index: dto.index,
      virtualProgramByDateIndex: dto.virtualProgramByDateIndex,
      socialPostId: dto.socialPostId,
      socialResume: dto.socialResume ? SocialPostResumeLightEntity.fromDto(dto.socialResume) : null,
      blocks: dto.blocks.map((block) => SessionBlockEntityFactory.fromDto(block)),
      sessionCompletedByUser: dto.sessionCompletedByUser,
    })
  }

  static fromEntityToFormik(entity: ProgramInfiniteSessionEntity): ProgramInfiniteSessionFormikType {
    return {
      _type: ProgramInfiniteSessionTypeOf,
      name: entity.name,
      description: entity.description,
      date: entity.date ? dayjs(entity.date).toDate() : null,
      programId: entity.programId,
      hideSessionContent: entity.hideSessionContent,
      cardioTag: entity.cardioTag,
      gymnasticTag: entity.gymnasticTag,
      weightliftingTag: entity.weightliftingTag,
      blocks: entity.blocks.map((block) => SessionBlockEntityFactory.fromEntityToFormik(block)),
      isTemplateSaved: false,
    }
  }

  static getDraftFormik(
    programId: string,
    defaultHideSessionContent: boolean | undefined,
    dateSession: string | number | undefined,
  ): ProgramInfiniteSessionFormikType {
    return {
      _type: ProgramInfiniteSessionTypeOf,
      name: word("program.session.name.default"),
      description: "",
      blocks: [],
      programId: programId,
      date: dateSession ? dayjs(dateSession).toDate() : null,
      hideSessionContent: defaultHideSessionContent ?? false,
      cardioTag: false,
      gymnasticTag: false,
      weightliftingTag: false,
      isTemplateSaved: false,
    }
  }

  static fromEntityToInputDto(entity: ProgramInfiniteSessionEntity): CreateSessionInputDto {
    return {
      // eslint-disable-next-line @typescript-eslint/no-misused-spread
      ...entity,
      date: dayjs(entity.date).format(defaultDateFormat),
      blocks: entity.blocks.map((block) => SessionBlockEntityFactory.fromEntityToInputDto(block)),
      duration: 0,
      restDay: false,
      index: null,
      scoringOption: null,
    }
  }

  static fromFormikToInputDto(formik: ProgramInfiniteSessionFormikType): CreateSessionInputDto {
    return {
      ...formik,
      date: dayjs(formik.date).format(defaultDateFormat),
      blocks: formik.blocks.map((block, index) => SessionBlockEntityFactory.fromFormikToInputDto(block, index)),
      duration: 0,
      restDay: false,
      index: null,
      scoringOption: null,
    }
  }

  static fromEntityToUpdateInputDto(entity: ProgramInfiniteSessionEntity): UpdateSessionInputDto {
    return {
      // eslint-disable-next-line @typescript-eslint/no-misused-spread
      ...entity,
      date: dayjs(entity.date).format(defaultDateFormat),
      blocks: entity.blocks.map((block) => SessionBlockEntityFactory.fromEntityToInputDto(block)),
      duration: 0,
      restDay: false,
      index: null,
      virtualProgramByDateIndex: null,
    }
  }

  static fromFormikToUpdateInputDto(formik: ProgramInfiniteSessionFormikType): UpdateSessionInputDto {
    return {
      ...formik,
      date: dayjs(formik.date).format(defaultDateFormat),
      blocks: formik.blocks.map((block, index) => SessionBlockEntityFactory.fromFormikToInputDto(block, index)),
      duration: 0,
      restDay: false,
      index: null,
      virtualProgramByDateIndex: null,
    }
  }
}
